import { tr } from "../i18n/LanguageContext";
import React from "react";
import { MapPin, Globe, ExternalLink, Lock } from "lucide-react";
import { getMeetingPlatformLabel } from "../data/meetingPlatforms";

/**
 * EventLocation
 *
 * Renders the right "where is this happening" block for an event:
 *   - offline → 📍 Location only
 *   - online  → 💻 Online Meeting (platform name + Join button)
 *   - hybrid  → both sections, with a friendly note that participants may
 *               attend either in person or online
 *
 * The Join Meeting button is gated by `canJoin`: only attendees who are
 * allowed to access the meeting see the actual link. Everyone else sees
 * a calm "Ссылка доступна после подтверждения" hint.
 *
 * `showDetails` is kept for backward compat with the events list, where
 * we render a compact version and never expose the join button.
 */
// Safe href for a meeting link: Telegram handles → t.me, http(s) as-is, anything else → null.
const toMeetingHref = (raw, platform) => {
  const v = (raw || "").trim();
  if (/\s/.test(v)) {
    const url = v.match(/https?:\/\/[^\s<>"']+/i);
    return url ? url[0].replace(/[).,;:!?]+$/, "") : null;
  }
  const m = v.match(/^(?:https?:\/\/)?@?([A-Za-z0-9_]{3,})\/?$/i);
  if (m && (v.startsWith("@") || v.includes("://") || platform === "telegram")) return `https://t.me/${m[1]}`;
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(v)) return null;
  return v && v.split("/")[0].includes(".") ? `https://${v}` : null;
};

const EventLocation = ({ event, showDetails = true, canJoin = false }) => {
  const type = (event?.type || "").toLowerCase();
  const platformLabel = getMeetingPlatformLabel(event?.meetingPlatform);
  const meetingLink = toMeetingHref(event?.onlineLink, event?.meetingPlatform);

  const physicalBlock = (
    <div className="flex items-start gap-2 text-sm" style={{ color: "var(--eunoia-soft)" }} data-testid="event-physical-location">
      <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "var(--eunoia-accent)" }} />
      <span style={{ color: "var(--eunoia-deep)" }}>
        {event?.location}{event?.city ? `, ${tr(event.city)}` : ""}
      </span>
    </div>
  );

  const onlineBlock = (
    <div className="flex flex-col gap-2" data-testid="event-online-meeting">
      <div className="flex items-start gap-2 text-sm" style={{ color: "var(--eunoia-soft)" }}>
        <Globe className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "#0ea5e9" }} />
        <span>
          <span style={{ color: "var(--eunoia-deep)", fontWeight: 500 }}>{tr("Онлайн встреча")}</span>
          <span className="mx-2" style={{ color: "var(--eunoia-soft)" }}>·</span>
          <span style={{ color: "var(--eunoia-deep)" }} data-testid="meeting-platform-label">
            {platformLabel}
          </span>
        </span>
      </div>

      {showDetails && (
        canJoin && meetingLink ? (
          <a
            href={meetingLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="join-meeting-btn"
            className="inline-flex items-center gap-2 self-start px-4 py-2 rounded-full text-sm font-medium transition-[transform,box-shadow] duration-300 ease-out hover:scale-[1.02] hover:shadow-md"
            style={{
              backgroundColor: "var(--eunoia-accent)",
              color: "white",
              textDecoration: "none",
            }}
          >
            <Globe className="h-4 w-4" />
            {tr("Присоединиться")}
            <ExternalLink className="h-3 w-3 opacity-80" />
          </a>
        ) : (
          <div
            className="inline-flex items-center gap-2 self-start px-3 py-2 rounded-full text-xs"
            data-testid="join-meeting-locked"
            style={{
              backgroundColor: "var(--eunoia-whisper)",
              color: "var(--eunoia-soft)",
            }}
          >
            <Lock className="h-3 w-3" />
            {tr("Ссылка доступна после подтверждения участия")}
          </div>
        )
      )}
    </div>
  );

  if (type === "hybrid") {
    return (
      <div className="space-y-3" data-testid="event-location-hybrid">
        {physicalBlock}
        {onlineBlock}
        <div
          className="text-xs px-3 py-2 rounded-full inline-block"
          style={{ color: "var(--eunoia-accent)", backgroundColor: "var(--eunoia-accent-whisper)" }}
        >
          {tr("Вы можете участвовать как лично, так и онлайн")}
        </div>
      </div>
    );
  }

  if (type === "online") {
    return <div data-testid="event-location-online">{onlineBlock}</div>;
  }

  // offline (default)
  return <div data-testid="event-location-offline">{physicalBlock}</div>;
};

export default EventLocation;
